import { Component, OnInit } from '@angular/core';
import { Receipt, ReceiptSort } from '@contracts/interfaces/receipt.interface';
import { MatDialog } from '@angular/material/dialog';
import { ChatGPTService } from 'src/app/services/chatgpt.service';
import { MediaService } from 'src/app/services/media.service';
import { ReceiptsService } from 'src/app/services/receipts.service';
import { TransactionsService } from 'src/app/services/transactions.service';
import { ACCOUNT } from 'src/app/accounts';
import { Transaction } from '@contracts/interfaces/transaction.interfaces';
import { MediaType } from '@contracts/interfaces/media.interfaces';
import { TransactionRangeDialogService } from 'src/app/dialogs/transaction-range/transaction-range.service.dialog';
import { ReceiptUploadDialogComponent } from 'src/app/dialogs/receipt-upload-dialog/receipt-upload.dialog';

@Component({
  selector: 'app-receipts',
  templateUrl: './receipts.component.html',
  styleUrls: ['./receipts.component.scss', '../table.scss']
})
export class ReceiptsComponent implements OnInit {

  public receipts: Receipt[] = [];

  public activeTab: string = "unsorted";
  public unsortedCount = 0;
  public sortedCount = 0;
  public deletedCount = 0;
  public isBulkRunningChatGPT = false;
  public bulkChatGPTCompleted = 0;
  public bulkChatGPTTotal = 0;
  public showAmountMismatchesOnly = false;
  public isLoadingSortedReceipts = false;
  private chatGPTBulkQueue: Receipt[] = [];
  private receiptRequestVersion = 0;
  private readonly audCurrencyFormatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  });


  constructor(
    private dialog: MatDialog,
    private receiptsService: ReceiptsService,
    private chatGPT: ChatGPTService,
    private mediaService: MediaService,
    private transactionService: TransactionsService,
    private transactionRangeDialog: TransactionRangeDialogService) {

    this.getUnsortedReceipts();
    this.refreshReceiptCounts();
  }

  getUnsortedReceipts() {
    this.activeTab = "unsorted";
    this.isLoadingSortedReceipts = false;
    const requestVersion = ++this.receiptRequestVersion;
    
    this.receiptsService.getReceipts(ReceiptSort.UNSORTED).subscribe((r: any) => {
      if (this.activeTab !== 'unsorted' || requestVersion !== this.receiptRequestVersion) {
        return;
      }

      this.receipts = r;
      this.refreshReceiptCounts();
    });
  }

  getSortedReceipts() {
    this.activeTab = "sorted";
    this.isLoadingSortedReceipts = true;
    const requestVersion = ++this.receiptRequestVersion;
    const amountMismatchOnly = this.showAmountMismatchesOnly;

    this.receiptsService.getReceipts(ReceiptSort.SORTED, { amountMismatchOnly }).subscribe({
      next: (r: Receipt[]) => {
        if (this.activeTab !== 'sorted' || requestVersion !== this.receiptRequestVersion) {
          return;
        }

        this.receipts = r;
        this.isLoadingSortedReceipts = false;
        this.refreshReceiptCounts();
      },
      error: () => {
        if (this.activeTab === 'sorted' && requestVersion === this.receiptRequestVersion) {
          this.isLoadingSortedReceipts = false;
        }
      }
    });
  }

  getDeletedReceipts() {
    this.activeTab = "deleted";
    this.isLoadingSortedReceipts = false;
    const requestVersion = ++this.receiptRequestVersion;

    this.receiptsService.getReceipts(ReceiptSort.DELETED).subscribe((r: any) => {
      if (this.activeTab !== 'deleted' || requestVersion !== this.receiptRequestVersion) {
        return;
      }

      this.receipts = r;
      this.refreshReceiptCounts();
    });
  }

  get displayedReceipts(): Receipt[] {
    if (this.activeTab !== 'sorted' || !this.showAmountMismatchesOnly || this.isLoadingSortedReceipts) {
      return this.receipts;
    }

    return this.receipts.filter(receipt => this.hasAmountMismatch(receipt));
  }

  trackByReceiptSha1(index: number, receipt: Receipt): string | number {
    return receipt.sha1 || index;
  }

  hasAmountMismatch(receipt: Receipt): boolean {
    const receiptAmount = this.getComparisonReceiptAmountInCents(receipt);
    const transactionAmount = this.getMatchedTransactionAmountInCents(receipt);

    if (receiptAmount === null || transactionAmount === null) {
      return false;
    }

    return Math.abs(receiptAmount) !== Math.abs(transactionAmount);
  }

  hasMultipleLinkedReceipts(receipt: Receipt): boolean {
    return Number(receipt.linkedReceiptCount) > 1;
  }

  formatLinkedReceiptTotal(receipt: Receipt): string {
    if (receipt.linkedReceiptAmountsComplete !== true) {
      return 'Incomplete';
    }

    const amount = this.getLinkedReceiptTotalInCents(receipt);
    return amount === null
      ? 'Unavailable'
      : this.audCurrencyFormatter.format(Math.abs(amount) / 100);
  }

  hasMatchedTransactionAmount(receipt: Receipt): boolean {
    return this.getMatchedTransactionAmountInCents(receipt) !== null;
  }

  formatMatchedTransactionAmount(receipt: Receipt): string {
    const amount = this.getMatchedTransactionAmountInCents(receipt);
    if (amount === null) {
      return 'Unavailable';
    }

    return this.audCurrencyFormatter.format(Math.abs(amount) / 100);
  }

  ngOnInit(): void {

  }

  openUploadDialog() {
    this.dialog.open(ReceiptUploadDialogComponent, {
      width: '820px',
      maxWidth: '95vw',
      autoFocus: false
    }).afterClosed().subscribe((uploaded: boolean) => {
      if (uploaded) {
        if (this.activeTab === 'sorted') {
          this.getSortedReceipts();
        } else if (this.activeTab === 'deleted') {
          this.getDeletedReceipts();
        } else {
          this.getUnsortedReceipts();
        }
      }
    });
  }

  editReceipt(receipt: Receipt) {
    if (!receipt.file) { 
      console.warn("Cannot edit receipt without file", receipt.sha1);
      return;
    }

    console.log(receipt.file)

    this.mediaService.getType(receipt.file).subscribe((type: MediaType) => {
      if (type == MediaType.IMAGE) {
        this.mediaService.edit(receipt.file, () => {});
      }
    });
  }

  openReceipt(receipt: Receipt) {
    if (!receipt.file) {
      console.warn("Cannot open receipt without file", receipt.sha1);
      return;
    }
    this.mediaService.open(receipt.file);
  }

  updateReceipt(receipt: Receipt) {
    console.log(receipt)
    this.receiptsService.updateReceipt(receipt).subscribe(r => {
      console.log("Updated receipt", r);
    });
  };

  logReceipt(receipt: Receipt) {
    console.log('Receipt info', receipt);
  }

  saveReceiptNote(receipt: Receipt) {
    const normalised = (receipt.note || '').trim();
    receipt.note = normalised;
    this.updateReceipt(receipt);
  }

  askChatGPT(receipt: Receipt, onComplete: () => void = () => {}) {
    if (!receipt.file) {
      console.warn("Cannot ask ChatGPT for receipt without file", receipt.sha1);
      onComplete();
      return;
    }

    if (this.isWaitingOnChatGPT(receipt) || this.chatGPTRequestShas.size >= 5) {
      onComplete();
      return;
    }

    this.setWaitingOnChatGPT(receipt);
    let finished = false;
    const finish = () => {
      if (finished) {
        return;
      }

      finished = true;
      this.clearWaitingOnChatGPT(receipt);
      onComplete();

      if (this.isBulkRunningChatGPT) {
        this.pumpChatGPTBulkQueue();
      }
    };

    this.mediaService.getType(receipt.file).subscribe({
      next: (type: MediaType) => {
        const chatGPTRequest = type === MediaType.IMAGE
          ? this.chatGPT.imageReceipt(receipt)
          : type === MediaType.PDF
            ? this.chatGPT.PDFReceipt(receipt)
            : null;

        if (!chatGPTRequest) {
          finish();
          return;
        }

        chatGPTRequest.subscribe({
          next: (response: any) => {
            if (!response?.reply) {
              finish();
              return;
            }

            this.applyChatGPTResponse(receipt, response);
            this.receiptsService.updateReceipt(receipt).subscribe({
              next: finish,
              error: finish
            });
          },
          error: finish
        });
      },
      error: finish
    });
  }

  bulkRunChatGPT() {
    if (this.isBulkRunningChatGPT || this.activeTab === 'deleted') {
      return;
    }

    this.chatGPTBulkQueue = this.receipts.filter(receipt =>
      !!receipt.file &&
      !receipt.missingFile &&
      !this.isWaitingOnChatGPT(receipt) &&
      !this.hasReceiptBeenProcessedByChatGPT(receipt)
    );

    if (!this.chatGPTBulkQueue.length) {
      return;
    }

    this.isBulkRunningChatGPT = true;
    this.bulkChatGPTCompleted = 0;
    this.bulkChatGPTTotal = this.chatGPTBulkQueue.length;
    this.pumpChatGPTBulkQueue();
  }

  private pumpChatGPTBulkQueue() {
    while (this.isBulkRunningChatGPT && this.chatGPTBulkQueue.length && this.chatGPTRequestShas.size < 5) {
      const receipt = this.chatGPTBulkQueue.shift();
      if (!receipt) {
        continue;
      }

      this.askChatGPT(receipt, () => {
        this.bulkChatGPTCompleted++;

        if (this.bulkChatGPTCompleted >= this.bulkChatGPTTotal) {
          this.isBulkRunningChatGPT = false;
          this.chatGPTBulkQueue = [];
          return;
        }

      });
    }
  }

  get hasReceiptsAvailableForChatGPT(): boolean {
    return this.chatGPTBulkAvailableCount > 0;
  }

  get chatGPTBulkAvailableCount(): number {
    if (this.activeTab === 'deleted') {
      return 0;
    }

    return this.receipts.filter(receipt =>
      !!receipt.file &&
      !receipt.missingFile &&
      !this.isWaitingOnChatGPT(receipt) &&
      !this.hasReceiptBeenProcessedByChatGPT(receipt)
    ).length;
  }

  hasReceiptBeenProcessedByChatGPT(receipt: Receipt): boolean {
    if (receipt.chatGPTProcessedAt) {
      return true;
    }

    const hasAmount = receipt.amount !== undefined &&
      receipt.amount !== null &&
      String(receipt.amount).trim() !== '';

    return !!receipt.date && hasAmount && !!(receipt.vender || '').trim();
  }

  private applyChatGPTResponse(receipt: Receipt, response: any) {
    receipt.amount = response.reply.total_amount;
    receipt.date = new Date(response.reply.date);
    receipt.vender = response.reply.vendor;
    receipt.lineItems = (response.reply.line_items || [])
      .map((item: any) => `${item.qty} x ${item.description} @ ${item.price}`);
    receipt.chatGPTProcessedAt = new Date();

  }

  findTransaction(receipt: Receipt) {

    if (!receipt.date) {
      return;
    }

    const date = new Date(receipt.date);
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);

    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    this.transactionRangeDialog.open(
      {
        start: dayStart,
        end: dayEnd
      },
      receipt,
      ACCOUNT.G72EXPENSES
    ).afterClosed().subscribe((transaction: Transaction | undefined) => {
      if (transaction) {
        this.updateTransaction(transaction, receipt);
      }
    });
  }

  updateTransaction(transaction: Transaction, receipt: Receipt) {
    // Found transaction, lets attach it.
    if (transaction.receipts == undefined)  {
      transaction.receipts = [];
    }

    // Make sure receipt does not already exist on transaction
    if (!this.receiptsService.hasReceipt(receipt, transaction)) {
      transaction.receipts.push(receipt);
    }

    transaction = this.receiptsService.removeDuplicateReceipts(transaction);

    receipt.transactionID = transaction.source.sha1;

    this.transactionService.update(transaction).subscribe(r => {

      this.receiptsService.updateReceipt(receipt).subscribe(r => {
        console.log("Updated receipt with transaction", r);
      });

      this.transactionService.openTransactionDialog(
        this.transactionService.getTransactionType(transaction),
        transaction.source.sha1,
        0,
        null,
        (r: any) => {
          console.log("Dialog closed", r);
        });
    })
  }

  receiptHasTransaction(receipt: Receipt): boolean {
    return receipt.transactionID != null;
  }

  showTransaction(receipt: Receipt) { 
      if (receipt.transactionID) {
        this.transactionService.openTransactionDialog(
          null,
          receipt.transactionID,
          0,
          null,
          (r: any) => {
            console.log("Dialog closed", r);
          });
    }
  }

  deleteReceipt(receipt: Receipt) {
    this.receiptsService.deleteReceipt(receipt).subscribe(r => {
      const index = this.receipts.findIndex(r => r.sha1 === receipt.sha1);
      if (index !== -1) {
        this.receipts.splice(index, 1);
      }
      this.refreshReceiptCounts();
    });
  }

  chatGPTRequestShas = new Set<string>();
  isWaitingOnChatGPT(receipt: Receipt): boolean {
    return this.chatGPTRequestShas.has(receipt.sha1);
  }

  setWaitingOnChatGPT(receipt: Receipt) {
    console.log("Setting waiting on ChatGPT for receipt", receipt.sha1);
    this.chatGPTRequestShas.add(receipt.sha1);
  }

  clearWaitingOnChatGPT(receipt: Receipt) {
    console.log("Clearing waiting on ChatGPT for receipt", receipt.sha1);
    this.chatGPTRequestShas.delete(receipt.sha1);
  }

  openTransactionRangeTest() {
    this.transactionRangeDialog.open();
  }

  private refreshReceiptCounts() {
    this.receiptsService.getReceiptCounts().subscribe(counts => {
      this.unsortedCount = counts.unsorted || 0;
      this.sortedCount = counts.sorted || 0;
      this.deletedCount = counts.deleted || 0;
    });
  }

  private getReceiptAmountInCents(receipt: Receipt): number | null {
    if (receipt.amount === undefined || receipt.amount === null || receipt.amount === '') {
      return null;
    }

    const normalisedAmount = String(receipt.amount).replace(/[^0-9.-]/g, '');
    const amount = Number(normalisedAmount);
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  }

  private getMatchedTransactionAmountInCents(receipt: Receipt): number | null {
    const amount = Number(receipt.matchedTransactionAmount);
    return receipt.matchedTransactionAmount !== null &&
      receipt.matchedTransactionAmount !== undefined &&
      Number.isFinite(amount)
      ? Math.round(amount)
      : null;
  }

  private getComparisonReceiptAmountInCents(receipt: Receipt): number | null {
    if (receipt.linkedReceiptCount !== null && receipt.linkedReceiptCount !== undefined) {
      return receipt.linkedReceiptAmountsComplete === true
        ? this.getLinkedReceiptTotalInCents(receipt)
        : null;
    }

    return this.getReceiptAmountInCents(receipt);
  }

  private getLinkedReceiptTotalInCents(receipt: Receipt): number | null {
    const amount = Number(receipt.linkedReceiptTotalAmount);
    return receipt.linkedReceiptTotalAmount !== null &&
      receipt.linkedReceiptTotalAmount !== undefined &&
      Number.isFinite(amount)
      ? Math.round(amount)
      : null;
  }
}
